'use client';

import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type PortalProps = {
  id: string;
  portalClassName?: string;
  children: ReactNode;
};

export const Portal = ({ id, portalClassName, children }: PortalProps) => {
  const [mounted, setMounted] = useState(false);
  const nodeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Create node only on client after mount
    const node = document.createElement('div');
    node.setAttribute('data-cy', `modal-${id}`);

    if (portalClassName) {
      node.className = portalClassName;
    }

    document.body.appendChild(node);
    nodeRef.current = node;
    setMounted(true);

    return () => {
      // Safe cleanup: check if node is still a child of body
      if (node.parentNode === document.body) {
        document.body.removeChild(node);
      }
    };
  }, [id, portalClassName]);

  // Update className if it changes
  useEffect(() => {
    if (nodeRef.current && portalClassName) {
      nodeRef.current.className = portalClassName;
    }
  }, [portalClassName]);

  // Don't render anything until mounted on client
  if (!mounted || !nodeRef.current) {
    return null;
  }

  return createPortal(children, nodeRef.current);
};
